// @greenlight-spec: 48-user-passthrough.md#one-app-registration-several-integrations
// @greenlight-spec: 28-it-dashboard-ui.md

import type { z } from 'zod';
import type { CreateIntegrationSchema } from '@greenlight/contract';
import {
  MICROSOFT_FABRIC_USER_PIECE_CATALOG_KEYS,
  MICROSOFT_FABRIC_USER_PIECES,
  applyUrlPlaceholders,
  type CatalogConnectionProfileDto,
  type CatalogEntryDto,
  type MicrosoftFabricUserPiece,
} from '@greenlight/shared';

import type { ReachabilityReport, ReachabilityTargets } from './RegisterConnectivityStep';
import {
  buildAuthConfig,
  composeClientCredentialsSecret,
  slugFromDisplayName,
  suggestedCatalogSlug,
} from './catalogRegistration';

type CreateIntegrationBody = z.input<typeof CreateIntegrationSchema>;

export interface FabricSqlEndpointDraft {
  host: string;
  database: string;
}

/** User passthrough (each signed-in person) or service identity (one Entra service principal). */
export type FabricIdentity = 'person' | 'app';

/** Semantic models refuse a service principal on a row-level-security model, so only a person reaches them. */
export function fabricPiecesFor(identity: FabricIdentity): readonly MicrosoftFabricUserPiece[] {
  return identity === 'person'
    ? MICROSOFT_FABRIC_USER_PIECES
    : MICROSOFT_FABRIC_USER_PIECES.filter((piece) => piece !== 'semantic_models');
}

export interface FabricRegistrationDraft {
  identity: FabricIdentity;
  tenantId: string;
  clientId: string;
  clientSecret: string;
  pieces: readonly MicrosoftFabricUserPiece[];
  sqlEndpoints: readonly FabricSqlEndpointDraft[];
}

export interface FabricRegistrationErrors {
  pieces?: string;
  tenantId?: string;
  clientId?: string;
  clientSecret?: string;
  sqlEndpoints?: Record<number, { host?: string; database?: string }>;
}

/** Catalog entries the Microsoft Fabric tile registers; the picker shows only the tile. */
export const FABRIC_FOLDED_CATALOG_KEYS: ReadonlySet<string> = new Set(
  MICROSOFT_FABRIC_USER_PIECES.filter((piece) => piece !== 'rest').map(
    (piece) => MICROSOFT_FABRIC_USER_PIECE_CATALOG_KEYS[piece],
  ),
);

/** The piece a catalog key registers, for opening the Fabric form from a request. */
export function fabricPieceFor(catalogKey: string): MicrosoftFabricUserPiece | null {
  return (
    MICROSOFT_FABRIC_USER_PIECES.find(
      (piece) => MICROSOFT_FABRIC_USER_PIECE_CATALOG_KEYS[piece] === catalogKey,
    ) ?? null
  );
}

// A Fabric SQL database's name ends in its item id, which adds nothing to a machine name.
const TRAILING_GUID_RE = /-[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const HOST_RE = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/i;

export function fabricRegistrationErrors(draft: FabricRegistrationDraft): FabricRegistrationErrors {
  const errors: FabricRegistrationErrors = {};
  if (draft.pieces.length === 0) errors.pieces = 'Tick at least one.';
  if (!UUID_RE.test(draft.tenantId.trim())) {
    errors.tenantId = 'Enter the Directory (tenant) ID as a GUID, not a domain name.';
  }
  if (draft.clientId.trim() === '') errors.clientId = 'Enter the Application (client) ID.';
  if (draft.clientSecret === '') errors.clientSecret = 'Paste the client secret.';
  if (draft.pieces.includes('sql')) {
    const rows: NonNullable<FabricRegistrationErrors['sqlEndpoints']> = {};
    const seen = new Set<string>();
    draft.sqlEndpoints.forEach((endpoint, index) => {
      const host = endpoint.host.trim().toLowerCase();
      const database = endpoint.database.trim();
      const row: { host?: string; database?: string } = {};
      if (!HOST_RE.test(host)) row.host = 'Enter the host from the SQL connection string.';
      if (database === '') row.database = 'Enter the database name.';
      const key = `${host}/${database.toLowerCase()}`;
      if (!row.host && !row.database && seen.has(key)) row.database = 'Already listed above.';
      seen.add(key);
      if (row.host || row.database) rows[index] = row;
    });
    if (Object.keys(rows).length > 0) errors.sqlEndpoints = rows;
  }
  return errors;
}

export function hasFabricRegistrationErrors(errors: FabricRegistrationErrors): boolean {
  return Object.keys(errors).length > 0;
}

export type FabricStep = 'setup' | 'connect' | 'credential' | 'review';

export const FABRIC_STEPS: readonly FabricStep[] = ['setup', 'connect', 'credential', 'review'];

const FABRIC_STEP_ERROR_KEYS: Record<FabricStep, readonly (keyof FabricRegistrationErrors)[]> = {
  setup: ['pieces'],
  connect: ['tenantId', 'sqlEndpoints'],
  credential: ['clientId', 'clientSecret'],
  review: [],
};

/** The errors that belong to one step of the Fabric stepper. */
export function fabricStepErrors(
  errors: FabricRegistrationErrors,
  step: FabricStep,
): FabricRegistrationErrors {
  return Object.fromEntries(
    Object.entries(errors).filter(([key]) =>
      FABRIC_STEP_ERROR_KEYS[step].includes(key as keyof FabricRegistrationErrors),
    ),
  ) as FabricRegistrationErrors;
}

/** The first step with an error, for Register to open. */
export function firstFabricStepWithErrors(errors: FabricRegistrationErrors): FabricStep | null {
  return (
    FABRIC_STEPS.find((step) => hasFabricRegistrationErrors(fabricStepErrors(errors, step))) ?? null
  );
}

const APP_AUTH_MODES = ['client_credentials', 'service_principal'];

function profileFor(entry: CatalogEntryDto, identity: FabricIdentity): CatalogConnectionProfileDto {
  const profile = entry.connection_profiles.find(
    (p) =>
      p.mvp_selectable &&
      (identity === 'person'
        ? p.auth_mode === 'user_delegated'
        : APP_AUTH_MODES.includes(p.auth_mode)),
  );
  if (!profile) throw new Error(`${entry.catalog_key} has no ${identity} profile`);
  return profile;
}

/** Every SQL endpoint is named for its database; a person's endpoint adds `-user`. */
function sqlBaseName(database: string, index: number, identity: FabricIdentity): string {
  const suffix = identity === 'person' ? '-user' : '';
  const slug = slugFromDisplayName(database) || String(index + 1);
  return `fabric-sql-${slug.slice(0, 63 - 'fabric-sql-'.length - suffix.length).replace(/-+$/, '')}${suffix}`;
}

function uniqueName(base: string, taken: Set<string>): string {
  let name = base.slice(0, 63);
  for (let n = 2; taken.has(name); n += 1) name = `${base.slice(0, 60)}-${n}`;
  taken.add(name);
  return name;
}

export interface FabricRegistration {
  /** Stable across resubmits of an unchanged row: the piece, or the SQL host and database. */
  key: string;
  body: CreateIntegrationBody;
}

/**
 * One ordinary registration per ticked piece, and one per SQL endpoint, sharing one
 * Entra client. A row already registered keeps the name it was registered under;
 * every other name is the catalog's suggestion (a SQL endpoint is named for its
 * database), numbered past anything taken.
 */
export function buildFabricRegistrations(
  draft: FabricRegistrationDraft,
  entries: readonly CatalogEntryDto[],
  takenNames: readonly string[],
  registered: Readonly<Record<string, string>>,
  acceptDataAccessTerms: boolean,
): FabricRegistration[] {
  const { identity } = draft;
  const person = identity === 'person';
  const taken = new Set([...takenNames, ...Object.values(registered)]);
  const tenant = { tenant_id: draft.tenantId.trim() };
  const clientId = draft.clientId.trim();
  const credential = (secret: string, authConfig?: { client_id: string }) => ({
    slug: 'access',
    scope: person
      ? 'Each signed-in person, through the Greenlight Fabric app registration.'
      : "Greenlight's Fabric service principal.",
    secret,
    ...(authConfig ? { authConfig } : {}),
  });
  const terms = acceptDataAccessTerms ? { acceptDataAccessTerms: true as const } : {};
  const asApp = person ? '' : ' (service principal)';
  const registrations: FabricRegistration[] = [];
  const nameFor = (key: string, base: string): string => registered[key] ?? uniqueName(base, taken);

  for (const piece of fabricPiecesFor(identity)) {
    if (!draft.pieces.includes(piece)) continue;
    const entry = entries.find(
      (e) => e.catalog_key === MICROSOFT_FABRIC_USER_PIECE_CATALOG_KEYS[piece],
    );
    if (!entry) continue;
    const profile = profileFor(entry, identity);
    const tokenUrl = applyUrlPlaceholders(profile.token_url ?? '', tenant, entry.placeholders);

    if (piece !== 'sql') {
      const key = `${identity}:${piece}`;
      registrations.push({
        key,
        body: {
          name: nameFor(key, suggestedCatalogSlug(entry, profile)),
          displayName: `${entry.display_name}${asApp}`,
          catalogKey: entry.catalog_key,
          baseUrl: entry.base_url,
          authMode: profile.auth_mode,
          authConfig: person
            ? {
                mechanism: 'authorization_code',
                authorize_url: applyUrlPlaceholders(
                  profile.authorize_url ?? '',
                  tenant,
                  entry.placeholders,
                ),
                token_url: tokenUrl,
                scopes: [...(profile.scopes ?? [])],
              }
            : buildAuthConfig(profile, { tokenUrl, scopes: [...(profile.scopes ?? [])] }),
          deliveryMode: 'proxied',
          credentials: [
            person
              ? credential(draft.clientSecret, { client_id: clientId })
              : credential(composeClientCredentialsSecret(clientId, draft.clientSecret)),
          ],
          ...terms,
        },
      });
      continue;
    }

    draft.sqlEndpoints.forEach((endpoint, index) => {
      const host = endpoint.host.trim().toLowerCase();
      const database = endpoint.database.trim();
      const shortName = database.replace(TRAILING_GUID_RE, '');
      const key = `${identity}:sql:${host}/${database.toLowerCase()}`;
      registrations.push({
        key,
        body: {
          name: nameFor(key, sqlBaseName(shortName, index, identity)),
          displayName: `Fabric SQL — ${shortName}${asApp}`,
          catalogKey: entry.catalog_key,
          authCategory: 'connected-database',
          authMode: profile.auth_mode,
          authConfig: person
            ? {
                mechanism: 'authorization_code',
                authorize_url: applyUrlPlaceholders(
                  profile.authorize_url ?? '',
                  tenant,
                  entry.placeholders,
                ),
                token_url: tokenUrl,
                scopes: [...(profile.scopes ?? [])],
              }
            : { client_id: clientId, tenant_id: tenant.tenant_id },
          deliveryMode: 'proxied',
          connection: {
            engine: 'fabric-warehouse',
            host,
            port: entry.default_port ?? 1433,
            database,
          },
          credentials: [
            person
              ? credential(draft.clientSecret, { client_id: clientId })
              : credential(draft.clientSecret),
          ],
          ...terms,
        },
      });
    });
  }
  return registrations;
}

/**
 * The addresses the registrations dial, one reachability request each (an API host
 * or a SQL endpoint, with the Entra token endpoint), with duplicates dropped.
 */
export function fabricReachabilityTargets(
  registrations: readonly FabricRegistration[],
): ReachabilityTargets[] {
  const seen = new Set<string>();
  const targets: ReachabilityTargets[] = [];
  for (const { body } of registrations) {
    const authConfig = body.authConfig as Record<string, unknown> | undefined;
    const tokenUrl = typeof authConfig?.['token_url'] === 'string' ? authConfig['token_url'] : '';
    const target: ReachabilityTargets = {
      ...(body.baseUrl ? { baseUrl: body.baseUrl } : {}),
      ...(body.connection
        ? { connection: { host: body.connection.host, port: body.connection.port } }
        : {}),
      ...(tokenUrl ? { tokenUrl } : {}),
    };
    const key = JSON.stringify(target);
    if (seen.has(key)) continue;
    seen.add(key);
    targets.push(target);
  }
  return targets;
}

/** One report for several requests: every address once, reachable only if all are. */
export function mergeReachabilityReports(
  reports: readonly ReachabilityReport[],
): ReachabilityReport {
  const seen = new Set<string>();
  const addresses = reports
    .flatMap((report) => report.addresses)
    .filter((a) => {
      const key = `${a.role}-${a.host}-${a.port}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  return {
    reachable: reports.every((report) => report.reachable),
    egress: reports[0]?.egress ?? 'cluster',
    advice: reports.find((report) => report.advice !== null)?.advice ?? null,
    addresses,
  };
}
